import { defineStore } from 'pinia'
import { ref } from 'vue'
import request from '@/utils/request'
import router from '@/router'
import { useNotificationStore } from '@/stores/notification'

export const useAuthStore = defineStore('auth', () => {
  // 兼容历史脏数据：localStorage 里可能被写入 "undefined"/"null"
  const normalizeToken = (raw) => {
    const t = (raw || '').trim()
    if (!t || t === 'undefined' || t === 'null') return ''
    return t
  }

  const token = ref(normalizeToken(localStorage.getItem('token')))
  const user = ref(null)

  const login = async (username, password) => {
    const requestData = { username, password }
    const response = await request.post('/auth/login', requestData)

    // 防御：后端异常/网关异常时，可能返回结构不完整，避免把 undefined 写进 localStorage
    const newToken = normalizeToken(response?.token)
    if (!newToken) {
      throw new Error('登录失败：未获取到有效 token')
    }

    token.value = newToken
    user.value = response.user
    localStorage.setItem('token', newToken)
    return response
  }

  const register = async (username, password, email) => {
    const requestData = { username, password, email }
    return await request.post('/auth/register', requestData)
  }

  const registerAdmin = async (username, password, email, inviteCode) => {
    const requestData = { username, password, email, inviteCode }
    return await request.post('/auth/register-admin', requestData)
  }

  const logout = () => {
    token.value = ''
    user.value = null
    localStorage.removeItem('token')
    try {
      const notificationStore = useNotificationStore()
      notificationStore.stopPolling()
      notificationStore.setUnreadCount(0)
    } catch {
      // 忽略 store 未加载等情况
    }
    router.push('/login')
  }

  const getCurrentUser = async () => {
    try {
      const response = await request.get('/auth/me')
      user.value = response
      return response
    } catch (error) {
      // token 无效/过期：清掉 token，强制回登录
      logout()
      throw error
    }
  }

  const isAdmin = () => {
    // 检查是否是管理员：ROLE_ADMIN 或 ROLE_SUPER_ADMIN
    const role = user.value?.role
    return role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN'
  }

  const isSuperAdmin = () => {
    return user.value?.role === 'ROLE_SUPER_ADMIN'
  }

  // 检查是否已登录：有 token 且有 user 信息
  const isAuthenticated = () => {
    return !!(token.value && user.value)
  }

  return {
    token,
    user,
    login,
    register,
    registerAdmin,
    logout,
    getCurrentUser,
    isAdmin,
    isSuperAdmin,
    isAuthenticated
  }
})
